import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CheckCircle,
    User,
    Mail,
    Phone,
    FileText,
    Briefcase,
    CreditCard,
    UserCheck,
    SearchX
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';

const ApprovalsList = ({ approvals, onApprove }) => {
    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-2">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <UserCheck className="h-5 w-5 text-yellow-500" />
                    Revisão e Aprovação
                </h3>
                <p className="text-gray-400">
                    Confirme os dados e aprove a entrada dos novos membros.
                </p>
            </div>

            <div className="space-y-4">
                {approvals.length > 0 ? (
                    approvals.map((reg) => (
                        <motion.div
                            key={reg.discordId}
                            layout
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all group"
                        >
                            <div className="p-6">
                                <div className="flex flex-col md:flex-row justify-between gap-6">
                                    {/* Nome e Info Básica */}
                                    <div className="flex items-center gap-5">
                                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-600 p-0.5 shadow-lg shadow-orange-500/20">
                                            <div className="w-full h-full bg-gray-950 rounded-[14px] flex items-center justify-center overflow-hidden">
                                                {reg.discordData.avatar ? (
                                                    <img
                                                        src={`https://cdn.discordapp.com/avatars/${reg.discordId}/${reg.discordData.avatar}.png`}
                                                        alt="Avatar"
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <UserCheck className="h-8 w-8 text-yellow-500" />
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors">
                                                {reg.personalData.fullName}
                                            </h3>
                                            <p className="text-sm text-gray-400 font-mono mt-1">{reg.discordId}</p>
                                            <div className="flex flex-wrap gap-2 mt-3">
                                                {reg.preStaffData.codename && (
                                                    <Badge variant="secondary" className="bg-white/10 text-white border-white/10 text-xs font-medium">
                                                        {reg.preStaffData.codename}
                                                    </Badge>
                                                )}
                                                <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 text-xs font-medium">
                                                    {reg.preStaffData.accessLevel}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Ações Rápidas */}
                                    <div className="flex items-center gap-3">
                                        <Button
                                            onClick={() => onApprove(reg.discordId)}
                                            className="h-12 px-8 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-green-900/20 transition-all duration-300"
                                        >
                                            Aprovar
                                            <CheckCircle className="h-5 w-5" />
                                        </Button>
                                    </div>
                                </div>

                                {/* Detalhes em Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8 pt-8 border-t border-white/5 text-sm">
                                    <div className="space-y-4">
                                        <p className="text-gray-500 font-bold uppercase text-[10px] tracking-widest flex items-center gap-2">
                                            <User className="w-3 h-3" />
                                            Dados Pessoais
                                        </p>
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3 text-gray-300 bg-white/5 p-2 rounded-lg border border-white/5">
                                                <Mail className="h-4 w-4 text-gray-500" />
                                                {reg.discordData.email}
                                            </div>
                                            <div className="flex items-center gap-3 text-gray-300 bg-white/5 p-2 rounded-lg border border-white/5">
                                                <Phone className="h-4 w-4 text-gray-500" />
                                                {reg.personalData.phone}
                                            </div>
                                            <div className="flex items-center gap-3 text-gray-300 bg-white/5 p-2 rounded-lg border border-white/5">
                                                <FileText className="h-4 w-4 text-gray-500" />
                                                CPF: {reg.personalData.cpf}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <p className="text-gray-500 font-bold uppercase text-[10px] tracking-widest flex items-center gap-2">
                                            <Briefcase className="w-3 h-3" />
                                            Dados da Empresa
                                        </p>
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3 text-gray-300 bg-white/5 p-2 rounded-lg border border-white/5">
                                                <span className="font-medium text-white">{reg.businessData.businessName}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-gray-300 bg-white/5 p-2 rounded-lg border border-white/5">
                                                <span className="font-mono text-xs">CNPJ: {reg.businessData.cnpj}</span>
                                            </div>
                                            <div className="text-gray-400 text-xs italic px-2">
                                                {reg.businessData.fullAddress}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <p className="text-gray-500 font-bold uppercase text-[10px] tracking-widest flex items-center gap-2">
                                            <CreditCard className="w-3 h-3" />
                                            Financeiro & Contrato
                                        </p>
                                        <div className="bg-black/20 rounded-xl p-4 border border-white/5 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-400">Banco</span>
                                                <span className="text-white font-medium">{reg.bankingData.bank}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-400">Valor</span>
                                                <span className="text-green-400 font-bold bg-green-500/10 px-2 py-0.5 rounded border border-green-500/20">
                                                    R$ {reg.contractData.contractValue || reg.preStaffData.contractValue}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-400">Vigência</span>
                                                <span className="text-blue-400 font-bold bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20">
                                                    {reg.contractData.contractMonths || reg.preStaffData.contractMonths} meses
                                                </span>
                                            </div>
                                            <div className="pt-2 border-t border-white/5">
                                                <p className="text-[10px] text-purple-400 uppercase font-bold mb-1">Chave PIX</p>
                                                <p className="text-xs text-white bg-purple-500/10 p-2 rounded border border-purple-500/10 font-mono break-all">{reg.bankingData.pixKey}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))
                ) : (
                    <div className="flex flex-col items-center justify-center py-24 text-center border border-white/5 rounded-2xl bg-white/[0.02]">
                        <div className="w-24 h-24 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center mb-6 shadow-inner ring-1 ring-white/5">
                            <CheckCircle className="h-10 w-10 text-gray-700" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Tudo em dia!</h3>
                        <p className="text-gray-500 max-w-sm mx-auto">
                            Não existem cadastros pendentes de aprovação no momento.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ApprovalsList;
